import { useMutation, useQuery } from '@apollo/client';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import * as React from 'react';
import { useState } from 'react';
import { FaCopy, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { EditSection } from '../../components/Layout';
import {
  copyPartnerSelfSignupConfigMutation,
  createPartnerSelfSignupConfigMutation,
  deletePartnerSelfSignupConfigMutation,
} from '../../graphql/mutations';
import { partnerSelfSignupConfigsQuery } from '../../graphql/queries';

export type ProductTransactionsGqlType = {
  article_number: number;
  name: string;
  gtin: string;
  price_cent: number;
};

export type ProductTransactionsUpdateGqlType = {
  article_number?: number;
  name?: string;
  gtin?: string;
  price_cent?: number;
};

export type ProductPurchaseGqlType = {
  article_number: number;
  name: string;
  gtin: string;
  price_cent: number;
};

export type ProductPurchaseUpdateGqlType = {
  article_number?: number;
  name?: string;
  gtin?: string;
  price_cent?: number;
};

export type ProductRentalGqlType = {
  name: string;
  price_cent: number;
};

export type OnboardingProductGqlType = {
  article_number?: number;
  name: string;
  gtin?: string;
  count?: number;
};

export type ProductRentalUpdateGqlType = {
  name?: string;
  price_cent?: number;
};

export type PartnerSelfSignupAddress = {
  zipcode: string;
  country: string;
  state?: string;
  cost_center?: string;
  poi_box?: string;
  invoice_email?: string;
  city: string;
  street: string;
};

export type PartnerSelfSignupConfigGqlType = {
  id: string;
  signup_code: string;
  onboarding_fee_cent: number | string;
  type: PartnerSelfSignupConfigTypeGqlType;
  company_name: string;
  products_transactions?: ProductTransactionsGqlType[];
  products_purchase?: ProductPurchaseGqlType[];
  products_rental?: ProductRentalGqlType[];
  cancelation_terms?: string;
  products_onboarding?: OnboardingProductGqlType[];
  billing_address?: PartnerSelfSignupAddress;
};

export enum PartnerSelfSignupConfigTypeGqlType {
  CatererGqlType = 'CATERER',
  KamGqlType = 'KAM',
}

export type PartnerSelfSignupConfigUpdateGqlType = {
  onboarding_fee_cent?: number;
  type?: PartnerSelfSignupConfigTypeGqlType;
  company_name?: string;
  products_transactions?: ProductTransactionsUpdateGqlType[];
  products_purchase?: ProductPurchaseUpdateGqlType[];
  products_rental?: ProductRentalUpdateGqlType[];
};

export default function SelfSignupConfigListView() {
  const [selfSignupConfigs, setSelfSignupConfigs] = useState<
    PartnerSelfSignupConfigGqlType[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signupCode, setSignupCode] = useState('');
  const [copyId, setCopyId] = useState('');
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteConfigMutation, { loading: deleting }] = useMutation(
    deletePartnerSelfSignupConfigMutation,
  );
  const [copyConfigMutation, { loading: copying }] = useMutation(
    copyPartnerSelfSignupConfigMutation,
  );
  const [createConfigMutation, { loading: creating }] = useMutation(
    createPartnerSelfSignupConfigMutation,
  );
  useQuery(partnerSelfSignupConfigsQuery, {
    onCompleted: (data) => {
      (setSelfSignupConfigs(data.getPartnerSelfSignUpConfigs),
        setIsLoading(false));
    },
  });

  function removePartnerSelfSignupConfig(id: string) {
    deleteConfigMutation({
      variables: { id },
      onCompleted: () => {
        const configs = [...selfSignupConfigs];
        const index = configs.findIndex((c) => c.id === id);
        configs.splice(index, 1);
        setSelfSignupConfigs([...configs]);
      },
    });
  }

  function copyPartnerSelfSignupConfig(id: string, signupCode: string) {
    copyConfigMutation({
      variables: { id, signup_code: signupCode },
      onCompleted: (data) => {
        const configs = [...selfSignupConfigs];
        configs.push(data.copyPartnerSelfSignupConfig);
        setSelfSignupConfigs(configs);
      },
    });
  }

  function createPartnerSelfSignupConfig(signupCode: string) {
    createConfigMutation({
      variables: { signup_code: signupCode },
      onCompleted: (data) => {
        const configs = [...selfSignupConfigs];
        configs.push(data.createPartnerSelfSignupConfig);
        setSelfSignupConfigs(configs);
      },
    });
  }

  function renderEnterSelfSignupCodeDialog() {
    if (!showCopyDialog && !showCreateDialog) {
      return null;
    }

    const hide = () => {
      setShowCopyDialog(false);
      setShowCreateDialog(false);
    };
    const execute = () => {
      if (signupCode.length != 0) {
        if (showCopyDialog) {
          copyPartnerSelfSignupConfig(copyId, signupCode);
        } else {
          createPartnerSelfSignupConfig(signupCode);
        }
      }
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          color="secondary"
          disabled={signupCode.length === 0}
        >
          {showCopyDialog ? 'Copy Config' : 'Create Config'}
        </Button>
        <Button onClick={hide} color="danger">
          Cancel
        </Button>
      </div>
    );

    return (
      <Dialog
        header={showCopyDialog ? 'Copy Config' : 'Create Config'}
        visible={showCopyDialog || showCreateDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label>Signup Code</Label>
            <Input
              autoComplete="new-password"
              type="text"
              placeholder={'asd1234'}
              value={signupCode}
              onChange={(e) => setSignupCode(e.target.value)}
            />
          </FormGroup>
        </Form>
      </Dialog>
    );
  }

  function renderCopy(id: string) {
    return (
      <Button
        className="ms-2"
        size="sm"
        onClick={() => {
          setShowCopyDialog(true);
          setCopyId(id);
        }}
      >
        <FaCopy />
      </Button>
    );
  }

  function tableHeader() {
    return (
      <div className="align-items-center export-buttons flex">
        <Button
          type="button"
          onClick={() => setShowCreateDialog(true)}
          className="p-button-success me-2"
        >
          <FaPlus />
        </Button>
      </div>
    );
  }

  function renderDelete(id: string) {
    return (
      <Button
        className="ms-2"
        color="danger"
        size="sm"
        onClick={() => removePartnerSelfSignupConfig(id)}
      >
        <FaTrashAlt />
      </Button>
    );
  }

  if (isLoading) {
    return <p>loading</p>;
  }

  return (
    <EditSection title="Self Signup Configs" isLoading={isLoading}>
      <DataTable
        header={tableHeader}
        value={selfSignupConfigs}
        dataKey="id"
        lazy
        loading={isLoading}
        className="p-datatable-striped"
        style={{ width: '100%', overflowX: 'scroll' }}
      >
        <Column
          headerStyle={{ width: '150px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => c.signup_code}
          header="Signup Code"
        />
        <Column
          headerStyle={{ width: '150px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => c.company_name}
          header="Company Name"
        />
        <Column
          headerStyle={{ width: '150px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => c.type}
          header="Type"
        />
        <Column
          headerStyle={{ width: '75px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => renderDelete(c.id)}
          header="Delete"
        />
        <Column
          headerStyle={{ width: '75px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => renderCopy(c.id)}
          header="Copy"
        />
        <Column
          headerStyle={{ width: '150px' }}
          body={(c: PartnerSelfSignupConfigGqlType) => (
            <a
              href={`/self-signup-config/${c.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </a>
          )}
          header="Link"
        />
      </DataTable>
      {renderEnterSelfSignupCodeDialog()}
    </EditSection>
  );
}
